/**
 * Engine plumes and nozzle glow at ENG_ sockets (style guide §3.6, §3.7, §9.4; D-047 lighting overhaul).
 * Asset-lane owned (task A11). Per ENG_MAIN_* / ENG_RETRO_* socket:
 *
 * - **Plume:** two additive shells on tapered tubes: a hot core that dies within the first 40 %, and a wider halo
 *   with a core-to-tail colour gradient (faction core colour at the throat, halo colour along the body, a deeper,
 *   dimmer tail), a soft volumetric edge from the view angle, flow-advected turbulence that grows downstream, shock
 *   diamonds that fade to their mean once they are smaller than a pixel, and a 12 Hz ±6 % flicker on boost. Length
 *   law: 0.5 nozzle diameters at idle, 3 at full thrust, 4 on boost (retro plumes are shorter). Brightness follows
 *   the E2 drive band (Y = 3 + 12 × thrust, × 1.4 on boost; materials.json `emissive`).
 * - **Nozzle glow:** a camera-facing sprite (halo ≥ 6 px at 1080p) visible within 100° of the exhaust axis, fading
 *   over the last 20°, so the heading cue survives to kilometres and without bloom (Low).
 * - **Heat shimmer (High):** a normal-blended shell around the plume that refracts the framebuffer copy
 *   (viewportSharedTexture) through flow noise, strongest near the nozzle.
 *
 * Materials are shared per library and drive colour pair; per-ship thrust, boost and flicker phase reach them through
 * a per-object vec4 uniform (userData.fxDrive), so any number of ships uses one pipeline per shell. The drive colours
 * are uniforms too, so every colour pair generates the same shader code and three's program cache (keyed by code)
 * gives all of them one pipeline per shell and pass (PERF PF-8). The shimmer has no colour: one material per library.
 * Every setter is allocation-free.
 */
import {
  AdditiveBlending,
  BackSide,
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  DynamicDrawUsage,
  FrontSide,
  Group,
  InstancedBufferAttribute,
  InstancedInterleavedBuffer,
  Matrix4,
  Mesh,
  MeshBasicNodeMaterial,
  NormalBlending,
  Sprite,
  SpriteNodeMaterial,
  Vector4,
  type Material,
  type Node,
  type Object3D,
  type Side,
  type UniformNode,
} from 'three/webgpu'
import {
  abs,
  cameraPosition,
  cameraProjectionMatrix,
  cos,
  dot,
  float,
  fwidth,
  instancedBufferAttribute,
  max,
  mix,
  modelViewMatrix,
  modelViewPosition,
  modelWorldMatrix,
  mx_fractal_noise_float,
  normalGeometry,
  normalView,
  normalize,
  positionGeometry,
  positionViewDirection,
  pow,
  saturate,
  screenUV,
  sin,
  smoothstep,
  uniform,
  uv,
  vec2,
  vec3,
  varying,
  vec4,
  viewportSharedTexture,
} from 'three/tsl'
import { logger } from '../../core/logger'
import type { ModelInstance } from '../../assets/loader'
import type { FactionId } from '../contracts'
import { paletteOf } from './data'
import { asV3, unitColorY } from './hull/surface'
import { internals, type MaterialLibrary } from './library'

export interface EngineFx {
  /** main and retro 0..1: actual thrust output fractions; boost lengthens the main plumes. */
  setThrust(main: number, retro: number, boost: boolean): void
  /** Ship-local thrust and torque fractions -1..1 that drive ENG_RCS_* puffs. */
  setControl(sway: number, heave: number, pitch: number, yaw: number, roll: number): void
  /** The plumes a plume pool draws (attached `pooled`), else empty. */
  readonly plumes: readonly PooledPlume[]
  dispose(): void
}

/** Plume tuning (style guide §3.7). */
export const PLUME = {
  /** Length in nozzle diameters at idle and at full thrust; boost multiplies by `boostLength`. */
  idleLength: 0.5,
  fullLength: 3,
  boostLength: 4 / 3,
  /** Retro plumes are this fraction of a main plume. */
  retroLength: 0.55,
  /** Share of the throat Y the halo and core shells carry. */
  haloY: 0.07,
  coreY: 0.22,
  /** Shock diamonds along the plume (count) and their strength at full thrust. */
  diamonds: 4.5,
  diamondGain: 0.45,
  /** Flow speed of the turbulence (plume lengths per second). */
  flow: 1.6,
  /** Boost flicker: 12 Hz, ±6 %. */
  flickerHz: 12,
  flickerAmp: 0.06,
  /** Glow sprite: world diameter per nozzle radius and the minimum halo size in pixels at 1080p. */
  glowSize: 1.7,
  glowHaloPx: 6,
  /** Visibility cone of the glow around the exhaust axis (degrees) and its fade. */
  coneDeg: 100,
  coneFadeDeg: 20,
  /** Heat shimmer: UV offset at the nozzle and the shell size relative to the plume. */
  shimmer: 0.0045,
  shimmerScale: 1.35,
} as const

/** Per-object drive: x thrust (0..1), y boost (0/1), z flicker phase (0..1), w kind (0 main, 1 retro). */
export const FX_DRIVE_KEY = 'fxDrive'

/** Plume length (metres) for a nozzle radius, thrust and boost (style guide §3.7 length law). */
export function plumeLength(radius: number, thrust: number, boost: boolean, retro: boolean): number {
  const d = 2 * radius
  const t = Math.min(Math.max(thrust, 0), 1)
  const len = d * (PLUME.idleLength + (PLUME.fullLength - PLUME.idleLength) * t) * (boost ? PLUME.boostLength : 1)
  return retro ? len * PLUME.retroLength : len
}

/** E2 drive band luminance at the throat (materials.json `emissive`). */
export function throatY(idleY: number, fullY: number, boostMult: number, thrust: number, boost: boolean): number {
  const t = Math.min(Math.max(thrust, 0), 1)
  return (idleY + (fullY - idleY) * t) * (boost ? boostMult : 1)
}

// ---------------------------------------------------------------------------------------------------------------
// Geometry: unit tubes along −Z (the socket's "out"), uv.y = 0 at the nozzle → 1 at the tail
// ---------------------------------------------------------------------------------------------------------------

/** Radius profile of the halo (a = 0 nozzle .. 1 tail): a slight bloom past the lip, then a long taper. */
export function haloRadius(a: number): number {
  const bloom = 1 + 0.18 * Math.sin(Math.min(a / 0.3, 1) * Math.PI * 0.5)
  return bloom * (1 - 0.72 * a ** 1.3)
}

/** Radius profile of the hot core over its own length (the core mesh spans the first 40 % of the plume). */
export function coreRadius(a: number): number {
  return 0.55 * (1 - a) ** 0.8
}

/** The hot core spans this share of the plume length. */
export const CORE_SHARE = 0.4

function tube(profile: (a: number) => number, segments: number, rings: number): BufferGeometry {
  const pos: number[] = []
  const nrm: number[] = []
  const uvs: number[] = []
  const idx: number[] = []
  for (let j = 0; j <= rings; j++) {
    const a = j / rings
    const r = profile(a)
    for (let i = 0; i <= segments; i++) {
      const t = (i / segments) * Math.PI * 2
      const c = Math.cos(t)
      const s = Math.sin(t)
      pos.push(c * r, s * r, -a)
      nrm.push(c, s, 0)
      uvs.push(i / segments, a)
    }
  }
  const row = segments + 1
  for (let j = 0; j < rings; j++)
    for (let i = 0; i < segments; i++) {
      const k = j * row + i
      idx.push(k, k + row, k + 1, k + 1, k + row, k + row + 1)
    }
  const g = new BufferGeometry()
  g.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3))
  g.setAttribute('normal', new BufferAttribute(new Float32Array(nrm), 3))
  g.setAttribute('uv', new BufferAttribute(new Float32Array(uvs), 2))
  g.setIndex(idx)
  g.computeBoundingSphere()
  return g
}

let haloGeo: BufferGeometry | null = null
let coreGeo: BufferGeometry | null = null

// ---------------------------------------------------------------------------------------------------------------
// Materials (shared per library and faction)
// ---------------------------------------------------------------------------------------------------------------

/** The per-object drive vec4 (the same Vector4 every frame: nothing allocates). */
function driveUniform(): UniformNode<'vec4', Vector4> {
  const fallback = new Vector4()
  return uniform(new Vector4()).onObjectUpdate(({ object }) => {
    const d = (object?.userData as Record<string, unknown> | undefined)?.[FX_DRIVE_KEY]
    return d instanceof Vector4 ? d : fallback
  })
}

interface PlumeMaterials {
  halo: Material
  core: Material
  glow: SpriteNodeMaterial
}

interface PlumeCache {
  /** Colour sets keyed by the resolved drive colours (engineCore/engineHalo): looks with one drive share them (M6 P23). */
  readonly sets: Map<string, PlumeMaterials>
  /** The heat shimmer (no colour), built on first use. */
  shimmer: Material | null
  /** The shimmer's pipelines are queued for warm-up (it only draws on High; PF-8). */
  shimmerWarmed: boolean
}

const caches = new WeakMap<MaterialLibrary, PlumeCache>()

function cacheOf(lib: MaterialLibrary): PlumeCache {
  let c = caches.get(lib)
  if (!c) {
    c = { sets: new Map(), shimmer: null, shimmerWarmed: false }
    caches.set(lib, c)
  }
  return c
}

/** A faction's drive colours; factions without drives (the derelict's first_rush) use the standard cyan drive. */
function driveColors(lib: MaterialLibrary, faction: FactionId): { readonly core: string; readonly halo: string } {
  const pal = paletteOf(internals(lib).data, faction)
  return {
    core: pal.engineCore === '#000000' ? '#EAF6FF' : pal.engineCore,
    halo: pal.engineHalo === '#000000' ? '#4CC3FF' : pal.engineHalo,
  }
}

/** The inputs a plume shell reads: per-ship uniforms and model matrices, or the pool's per-instance attributes. */
interface ShellSource {
  /** x thrust, y boost, z flicker phase (FX_DRIVE_KEY). */
  readonly drive: Node<'vec4'>
  /** Linear drive colours. */
  readonly coreC: Node<'vec3'>
  readonly haloC: Node<'vec3'>
  /** The unit view-space surface normal. */
  readonly normalV: Node<'vec3'>
  /** The unit view-space exhaust axis (the tube's −Z). */
  readonly axisView: Node<'vec3'>
}

/** A plume halo or core shell over `src` (shared by the per-ship materials and the plume pool, PERF L-M4). */
function shellMaterial(lib: MaterialLibrary, kind: 'halo' | 'core', name: string, src: ShellSource): MeshBasicNodeMaterial {
  const li = internals(lib)
  const e = li.data.emissive
  const time = li.time
  const { drive, coreC, haloC } = src
  const m = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending, side: DoubleSide })
  m.name = name
  // PERF L-M4: additive with no depth write, so both faces in one pass give the two-pass picture with one draw.
  m.forceSinglePass = li.data.plumes.singlePassShells
  const a = uv().y
  const thrust = drive.x
  const boost = drive.y
  // E2 band at the throat.
  const y = mix(float(e.engineIdleY), float(e.engineFullY), thrust).mul(mix(float(1), float(e.boostMult), boost))
  // Soft volumetric edge: both faces add, brightest where the view crosses the most plume.
  const ndv = abs(dot(src.normalV, positionViewDirection))
  // Soft silhouettes (no hard shell outline), and the shells thin out when the plume is seen end-on: the nozzle
  // glow sprite carries that view, and stacked shell walls would read as rings.
  const edge = pow(smoothstep(kind === 'core' ? 0.25 : 0.08, 0.95, ndv), kind === 'core' ? 2.5 : 1.8)
  const endOn = pow(abs(dot(src.axisView, positionViewDirection)), 3)
  const side = float(1).sub(endOn.mul(kind === 'core' ? 0.85 : 0.6))
  // Flow-advected turbulence (growing downstream) in the plume's own cylinder coordinates.
  const ang = uv().x.mul(Math.PI * 2)
  const flowP = vec3(cos(ang).mul(1.3), sin(ang).mul(1.3), a.mul(5).sub(time.mul(PLUME.flow * 5)).add(drive.z.mul(17)))
  const n = mx_fractal_noise_float(flowP, 3, 2, 0.5).mul(0.5).add(0.5)
  const turb = mix(float(1), n.mul(1.5).add(0.25), smoothstep(0.05, 0.7, a))
  // Shock diamonds: periodic bright rings, filtered to their mean once they shrink under a pixel.
  const phase = a.mul(PLUME.diamonds)
  const ring = pow(float(0.5).add(cos(phase.mul(Math.PI * 2)).mul(0.5)), 6)
  const rings = mix(ring, float(0.25), smoothstep(0.25, 0.6, fwidth(phase)))
  const diamonds = float(1).add(rings.mul(PLUME.diamondGain).mul(thrust).mul(float(1).sub(a)))
  // 12 Hz boost flicker.
  const flicker = float(1).add(boost.mul(PLUME.flickerAmp).mul(sin(time.mul(PLUME.flickerHz * Math.PI * 2).add(drive.z.mul(6.283)))))
  // Axial falloff with a short soft start at the lip (no seam against the nozzle). The core mesh is scaled to the
  // first 40 % of the plume, so its own uv.y reaches 1 there.
  const fall = pow(float(1).sub(a), kind === 'core' ? 1.6 : 1.5).mul(smoothstep(0, kind === 'core' ? 0.08 : 0.035, a))
  const body = mix(coreC, haloC, smoothstep(0, 0.28, a))
  const tail = haloC.mul(vec3(0.55, 0.7, 1.15))
  const col = kind === 'core' ? unitColorY(coreC, y.mul(PLUME.coreY)) : unitColorY(mix(body, tail, smoothstep(0.4, 1, a)), y.mul(PLUME.haloY))
  m.colorNode = col.mul(edge).mul(side).mul(turb).mul(diamonds).mul(flicker).mul(fall)
  return m
}

/**
 * The nozzle glow's colour: E2 core with a halo and the exhaust-axis visibility cone (`axis` the unit world exhaust
 * direction, `toCam` the unit world direction to the camera). Shared by the per-ship sprite and the plume pool.
 */
function glowColor(lib: MaterialLibrary, drive: Node<'vec4'>, coreC: Node<'vec3'>, haloC: Node<'vec3'>, axis: Node<'vec3'>, toCam: Node<'vec3'>): Node<'vec3'> {
  const e = internals(lib).data.emissive
  const d = uv().sub(0.5).length().mul(2)
  const core = pow(saturate(float(1).sub(d.mul(3.2))), 2)
  const halo = pow(saturate(float(1).sub(d)), 3).mul(0.35)
  const cone = smoothstep(Math.cos((PLUME.coneDeg * Math.PI) / 180), Math.cos(((PLUME.coneDeg - PLUME.coneFadeDeg) * Math.PI) / 180), dot(axis, toCam))
  const thrust = drive.x
  const y = mix(float(e.engineIdleY), float(e.engineFullY), thrust).mul(mix(float(1), float(e.boostMult), drive.y))
  const on = smoothstep(0, 0.05, thrust)
  const tint = mix(haloC, coreC, core)
  return unitColorY(tint, y.mul(0.3)).mul(core.add(halo)).mul(cone).mul(on)
}

function buildMaterials(lib: MaterialLibrary, faction: FactionId, coreHex: string, haloHex: string): PlumeMaterials {
  // Colours as uniforms (PF-8): the same linear values the old `color(hex)` constants carried, but outside the code.
  const coreC: Node<'vec3'> = asV3(uniform(new Color(coreHex)))
  const haloC: Node<'vec3'> = asV3(uniform(new Color(haloHex)))

  const shell = (kind: 'halo' | 'core'): Material =>
    shellMaterial(lib, kind, `plume:${kind}:${faction}`, {
      drive: driveUniform(),
      coreC,
      haloC,
      normalV: normalize(normalView),
      axisView: normalize(modelViewMatrix.mul(vec4(0, 0, -1, 0)).xyz),
    })

  // Nozzle glow sprite: E2 core with a halo, a minimum pixel size and the exhaust-axis visibility cone.
  const glowDrive = driveUniform()
  const glow = new SpriteNodeMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending })
  glow.name = `plume:glow:${faction}`
  {
    const worldSize = modelWorldMatrix.mul(vec4(1, 0, 0, 0)).xyz.length()
    // World metres per 1080p pixel at the sprite's depth (P[1][1] = 1 / tan(fov / 2)).
    const p11 = cameraProjectionMatrix.mul(vec4(0, 1, 0, 0)).y
    const pxToWorld = modelViewPosition.z.negate().mul(2).div(p11.mul(1080))
    glow.scaleNode = max(float(1), pxToWorld.mul(PLUME.glowHaloPx * 2).div(max(worldSize, 1e-4)))
    const axis = normalize(modelWorldMatrix.mul(vec4(0, 0, 1, 0)).xyz).negate()
    const toCam = normalize(cameraPosition.sub(modelWorldMatrix.mul(vec4(0, 0, 0, 1)).xyz))
    glow.colorNode = glowColor(lib, glowDrive, coreC, haloC, axis, toCam)
    glow.opacityNode = float(1)
  }
  return { halo: shell('halo'), core: shell('core'), glow }
}

/** Heat shimmer: refract the framebuffer copy through flow noise, strongest near the nozzle and the plume axis. */
function buildShimmer(lib: MaterialLibrary): Material {
  const time = internals(lib).time
  const shimmerDrive = driveUniform()
  const shimmer = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: NormalBlending, side: DoubleSide })
  shimmer.name = 'plume:shimmer'
  const a = uv().y
  const ang = uv().x.mul(Math.PI * 2)
  const p = vec3(cos(ang).mul(2), sin(ang).mul(2), a.mul(9).sub(time.mul(PLUME.flow * 9)))
  const nx = mx_fractal_noise_float(p, 2, 2, 0.5)
  const ny = mx_fractal_noise_float(p.add(vec3(17.3, 4.1, 9.7)), 2, 2, 0.5)
  const ndv = abs(dot(normalize(normalView), positionViewDirection))
  const strength = shimmerDrive.x.mul(pow(float(1).sub(a), 1.5)).mul(pow(ndv, 1.5))
  const offset = vec2(nx, ny).mul(PLUME.shimmer).mul(strength)
  shimmer.colorNode = viewportSharedTexture(screenUV.add(offset)).rgb
  shimmer.opacityNode = saturate(strength.mul(4))
  return shimmer
}

function shimmerFor(lib: MaterialLibrary): Material {
  const c = cacheOf(lib)
  return (c.shimmer ??= buildShimmer(lib))
}

/** The plume colour set of a faction's drive (exported for the shared-program test). */
export function plumeMaterialsFor(lib: MaterialLibrary, faction: FactionId): Readonly<PlumeMaterials> {
  const sets = cacheOf(lib).sets
  const { core, halo } = driveColors(lib, faction)
  const key = `${core}:${halo}`
  let m = sets.get(key)
  if (!m) {
    m = buildMaterials(lib, faction, core, halo)
    sets.set(key, m)
  }
  return m
}

// ---------------------------------------------------------------------------------------------------------------
// Instances
// ---------------------------------------------------------------------------------------------------------------

/**
 * One plume as the plume pool reads it (PERF L-M4 part 3). `group` hangs at its socket (identity) and is visible while
 * the plume burns; `len` is the current halo length (metres, shell space) and `drive` the FX_DRIVE_KEY vec4.
 */
export interface PooledPlume {
  readonly group: Group
  readonly radius: number
  readonly retro: boolean
  readonly drive: Vector4
  readonly len: number
  /** The drive colours (sRGB hex). */
  readonly coreHex: string
  readonly haloHex: string
}

interface Plume extends PooledPlume {
  /** Null when the plume pool draws the shells and the glow (`pooled`). */
  readonly halo: Mesh | null
  readonly core: Mesh | null
  readonly shimmer: Mesh
  readonly glow: Sprite | null
  len: number
}

let phaseSeq = 0

function fxMesh(geo: BufferGeometry, material: Material, name: string, order: number): Mesh {
  const m = new Mesh(geo, material)
  m.name = name
  m.castShadow = false
  m.receiveShadow = false
  m.frustumCulled = false
  m.renderOrder = order
  return m
}

function addPlumes(
  lib: MaterialLibrary,
  faction: FactionId,
  sockets: ReadonlyMap<string, Object3D>,
  prefix: string,
  retro: boolean,
  pooled: boolean,
  out: Plume[],
): void {
  haloGeo ??= tube(haloRadius, 24, 24)
  coreGeo ??= tube(coreRadius, 16, 12)
  const mats = plumeMaterialsFor(lib, faction)
  const colors = driveColors(lib, faction)
  const shimmerMat = shimmerFor(lib)
  for (const [name, node] of sockets) {
    if (!name.startsWith(prefix)) continue
    const r = typeof node.userData.radius === 'number' ? node.userData.radius : 0.5
    phaseSeq = (phaseSeq + 1) % 1000
    const drive = new Vector4(0, 0, (phaseSeq * 0.6180339887) % 1, retro ? 1 : 0)
    const group = new Group()
    group.name = `plume:${name}`
    const shimmer = fxMesh(haloGeo, shimmerMat, `plume:shimmer:${name}`, 9)
    shimmer.userData[FX_DRIVE_KEY] = drive
    group.add(shimmer)
    group.visible = false
    node.add(group)
    const base = { group, shimmer, radius: r, retro, drive, len: 0, coreHex: colors.core, haloHex: colors.halo }
    if (pooled) {
      out.push({ ...base, halo: null, core: null, glow: null })
      continue
    }
    const halo = fxMesh(haloGeo, mats.halo, `plume:halo:${name}`, 10)
    const core = fxMesh(coreGeo, mats.core, `plume:core:${name}`, 11)
    const glow = new Sprite(mats.glow)
    glow.name = `plume:glow:${name}`
    glow.renderOrder = 12
    glow.frustumCulled = false
    glow.scale.setScalar(r * PLUME.glowSize)
    for (const o of [halo, core, glow]) o.userData[FX_DRIVE_KEY] = drive
    group.add(halo, core, glow)
    out.push({ ...base, halo, core, glow })
  }
}

/**
 * The drive amount reaches `drivePlumes` through this scratch, not as an argument (D-014; PERF L-M4 part 3): a
 * fractional thrust passed to a call V8 doesn't inline is boxed into a fresh 16 B heap number each time (the
 * optimized caller holds it unboxed), which was 16 B per lit ship view per frame. A typed-array slot never boxes.
 */
const driveAmount = new Float64Array(1)

function drivePlumes(plumes: readonly Plume[], boost: boolean, shimmerOn: boolean): void {
  const amount = driveAmount[0]
  for (let i = 0; i < plumes.length; i++) {
    const p = plumes[i]
    const on = amount > 0.01
    p.group.visible = on
    if (!on) continue
    const b = boost && !p.retro
    p.drive.x = Math.min(amount, 1)
    p.drive.y = b ? 1 : 0
    const len = plumeLength(p.radius, amount, b, p.retro)
    p.len = len
    p.halo?.scale.set(p.radius, p.radius, len)
    p.core?.scale.set(p.radius, p.radius, len * CORE_SHARE)
    p.shimmer.scale.set(p.radius * PLUME.shimmerScale, p.radius * PLUME.shimmerScale, len * 1.25)
    p.shimmer.visible = shimmerOn
  }
}

/**
 * The shells are transparent and double-sided, so three draws them in two passes, back faces then front faces, with
 * `side` switched to BackSide and then FrontSide (Renderer._renderTransparents). Those are the pipelines to warm.
 * three r186's `compileAsync` queues the two passes but builds them after `side` is back to DoubleSide, so it warms a
 * DoubleSide pipeline nothing draws. A clone with a fixed side generates the same shader code and pipeline key as the
 * live pass, so it lands in the same caches. The clones are never disposed (disposing would release shared pipelines).
 */
const SHELL_PASSES: readonly Side[] = [BackSide, FrontSide]

function sided(m: Material, side: Side): Material {
  const c = m.clone()
  c.side = side
  return c
}

/** The two shimmer shells (one per shell pass) at full drive, added to a warm holder. Marks the library warmed. */
function addShimmerParts(lib: MaterialLibrary, holder: Group): void {
  const halo = (haloGeo ??= tube(haloRadius, 24, 24))
  const shimmer = shimmerFor(lib)
  const drive = new Vector4(1, 1, 0, 0)
  for (const side of SHELL_PASSES) {
    const m = fxMesh(halo, sided(shimmer, side), 'plume:warm:shimmer', 9)
    m.userData[FX_DRIVE_KEY] = drive
    holder.add(m)
  }
  cacheOf(lib).shimmerWarmed = true
}

/**
 * A detached holder with one lit plume set (halo and core shells and the glow sprite) per distinct drive colour set
 * of `factions`, for the pipeline warm-up (render/reachFx/warmup.ts). Plumes stay hidden until a ship thrusts, so
 * without it the first thrust built their pipelines mid-frame: a 0.45–0.78 s stall (D-TBD, 2026-10-05). The shells use
 * the live shared geometry and materials, so their pipelines are the live ones. The colour sets share one program per
 * shell (PF-8), so only the first set builds pipelines; the rest build their node states. The heat shimmer draws only on
 * High, so it is added only when the library's preset is High and it is not queued yet (`buildShimmerWarmHolder`
 * covers a later switch to High). Rare: allocates.
 */
export function buildPlumeWarmHolder(lib: MaterialLibrary, factions: readonly FactionId[]): Group {
  const halo = (haloGeo ??= tube(haloRadius, 24, 24))
  const core = (coreGeo ??= tube(coreRadius, 16, 12))
  const holder = new Group()
  holder.name = 'plume:warm'
  const seen = new Set<Readonly<PlumeMaterials>>()
  for (const faction of factions) {
    const mats = plumeMaterialsFor(lib, faction)
    if (seen.has(mats)) continue
    seen.add(mats)
    // Full thrust and boost: the drive is a uniform, so any drive state shares these pipelines.
    const drive = new Vector4(1, 1, 0, 0)
    const parts: Object3D[] = [new Sprite(mats.glow)]
    // Single-pass shells (PERF L-M4) draw as they are, so the holder warms them as they are; two-pass shells warm
    // one fixed-side clone per pass.
    if (mats.halo.forceSinglePass) parts.push(fxMesh(halo, mats.halo, 'plume:warm:halo', 10), fxMesh(core, mats.core, 'plume:warm:core', 11))
    else {
      for (const side of SHELL_PASSES) {
        parts.push(fxMesh(halo, sided(mats.halo, side), 'plume:warm:halo', 10), fxMesh(core, sided(mats.core, side), 'plume:warm:core', 11))
      }
    }
    for (const o of parts) o.userData[FX_DRIVE_KEY] = drive
    holder.add(...parts)
  }
  if (internals(lib).data.plumes.pooled && factions.length > 0) holder.add(plumePoolWarmGroup(lib, factions[0]))
  if (internals(lib).preset === 'high' && !cacheOf(lib).shimmerWarmed) addShimmerParts(lib, holder)
  return holder
}

/**
 * The heat shimmer's warm holder for a switch to High (`applyQuality`), or null when the preset is not High or the
 * shimmer is already queued for this library (once per library: compiled pipelines stay cached). Rare: allocates.
 */
export function buildShimmerWarmHolder(lib: MaterialLibrary): Group | null {
  if (internals(lib).preset !== 'high' || cacheOf(lib).shimmerWarmed) return null
  const holder = new Group()
  holder.name = 'plume:warm:shimmer'
  addShimmerParts(lib, holder)
  return holder
}

// ---------------------------------------------------------------------------------------------------------------
// The plume pool (PERF L-M4 part 3)
// ---------------------------------------------------------------------------------------------------------------

/**
 * PERF L-M4 part 3: every ship view's plume halo and core shells and nozzle glows in three instanced draws (one per
 * family, every faction together: the drive colours are per-instance data). Each instance carries the plume's pose in
 * render space, built from its root's **current** pose (`root.updateMatrix()` × the plume's offset cached at `add`,
 * never `matrixWorld`: PF-5a's trailing-light lesson), its shell axes scaled (r, r, len), the drive (thrust, boost,
 * phase, retro), the linear core and halo colours and the glow's world size. A plume that is unlit or hidden, or whose
 * root has left the pool's parent, draws at size 0. The draws are additive, depth-write off, on render orders 10–12 as
 * the per-ship meshes, so one draw per family adds the same light. The heat shimmer stays per plume.
 */
export interface PlumePool {
  /** The three draws (halo, core, glow). Add it to the ship roots' parent (render space); keep its transform identity. */
  readonly object: Group
  readonly capacity: number
  /** Live plumes; instances 0..count-1 are dense. */
  readonly count: number
  /** Instance data (read-only for callers): origin xyz; shell axes x, y, z (9 floats, scaled; zero = hidden). */
  readonly origins: Float32Array
  readonly axes: Float32Array
  /** (thrust, boost, phase, retro) per instance. */
  readonly drives: Float32Array
  /** Linear core rgb, then halo rgb (6 floats per instance). */
  readonly colors: Float32Array
  /** The glow's world diameter (0 = hidden). */
  readonly glowSizes: Float32Array
  /** Adds a plume of a view under `root` (its group rigid on the hull: the offset is cached here). A slot, or -1 when full. */
  add(root: Object3D, plume: PooledPlume): number
  /** Frees a slot (swap-remove: the last instance moves into its place). Unknown or freed slots are ignored. */
  remove(slot: number): void
  /** Writes every live plume's pose, drive and glow size from its root's current pose and drive. Allocation-free. */
  update(): void
  dispose(): void
}

const PLUME_POOL_NAME = 'plume:pool'
const poolColor = new Color()
const poolMatrix = new Matrix4()

/** True when every node from `node` up to `root` is visible. */
function shownUnder(node: Object3D, root: Object3D): boolean {
  for (let o: Object3D | null = node; o !== null; o = o.parent) {
    if (!o.visible) return false
    if (o === root) return true
  }
  return false
}

function dynamicAttr(a: Float32Array, size: number): InstancedBufferAttribute {
  return new InstancedBufferAttribute(a, size).setUsage(DynamicDrawUsage)
}

export function createPlumePool(lib: MaterialLibrary, capacity: number): PlumePool {
  const cap = Math.max(1, Math.floor(capacity))
  const haloG = (haloGeo ??= tube(haloRadius, 24, 24))
  const coreG = (coreGeo ??= tube(coreRadius, 16, 12))
  const originArr = new Float32Array(cap * 3)
  const axisArr = new Float32Array(cap * 9)
  const driveArr = new Float32Array(cap * 4)
  const colorArr = new Float32Array(cap * 6)
  const glowArr = new Float32Array(cap)
  const aOrigin = dynamicAttr(originArr, 3)
  const axisBuf = new InstancedInterleavedBuffer(axisArr, 9).setUsage(DynamicDrawUsage)
  const colorBuf = new InstancedInterleavedBuffer(colorArr, 6).setUsage(DynamicDrawUsage)
  const aDrive = dynamicAttr(driveArr, 4)
  const aGlow = dynamicAttr(glowArr, 1)
  /** Per dense index: the plume group's offset in root space (3×4, column-major), its root and its record. */
  const offset = new Float64Array(cap * 12)
  const roots = new Array<Object3D | null>(cap).fill(null)
  const plumes = new Array<PooledPlume | null>(cap).fill(null)
  /** Dense index → slot handle, slot handle → dense index (-1 = free), and the free-handle stack. */
  const handleOf = new Int32Array(cap)
  const indexOf = new Int32Array(cap).fill(-1)
  const free = new Int32Array(cap)
  for (let i = 0; i < cap; i++) free[i] = cap - 1 - i
  let freeTop = cap
  let n = 0
  let warned = false

  // Per-instance nodes; the fragment stage reads them through varyings.
  const origin = instancedBufferAttribute<'vec3'>(aOrigin, 'vec3')
  const ax = instancedBufferAttribute<'vec3'>(axisBuf, 'vec3', 9, 0)
  const ay = instancedBufferAttribute<'vec3'>(axisBuf, 'vec3', 9, 3)
  const az = instancedBufferAttribute<'vec3'>(axisBuf, 'vec3', 9, 6)
  const drive = varying(instancedBufferAttribute<'vec4'>(aDrive, 'vec4'), 'vPlumeDrive')
  const coreC = varying(instancedBufferAttribute<'vec3'>(colorBuf, 'vec3', 6, 0), 'vPlumeCore')
  const haloC = varying(instancedBufferAttribute<'vec3'>(colorBuf, 'vec3', 6, 3), 'vPlumeHalo')

  const shellMesh = (kind: 'halo' | 'core', geo: BufferGeometry, order: number): Mesh => {
    const zAxis = az.mul(kind === 'core' ? CORE_SHARE : 1)
    const p = positionGeometry
    const nrm = normalGeometry
    // The local normal through the inverse transpose of (ax | ay | zAxis), orthogonal columns: column / |column|².
    const nLocal = ax
      .mul(nrm.x.div(max(dot(ax, ax), 1e-12)))
      .add(ay.mul(nrm.y.div(max(dot(ay, ay), 1e-12))))
      .add(zAxis.mul(nrm.z.div(max(dot(zAxis, zAxis), 1e-12))))
    const m = shellMaterial(lib, kind, `${PLUME_POOL_NAME}:${kind}`, {
      drive,
      coreC,
      haloC,
      normalV: normalize(varying(modelViewMatrix.mul(vec4(nLocal, 0)).xyz, 'vPlumeNormal')),
      axisView: normalize(varying(modelViewMatrix.mul(vec4(az.negate(), 0)).xyz, 'vPlumeAxis')),
    })
    m.positionNode = origin.add(ax.mul(p.x)).add(ay.mul(p.y)).add(zAxis.mul(p.z))
    const mesh = fxMesh(geo, m, `${PLUME_POOL_NAME}:${kind}`, order)
    mesh.count = 0
    return mesh
  }
  const haloMesh = shellMesh('halo', haloG, 10)
  const coreMesh = shellMesh('core', coreG, 11)

  // The glow: the per-ship sprite's minimum pixel size and cone per instance (the light pool's instanced Sprite).
  const glowM = new SpriteNodeMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending })
  glowM.name = `${PLUME_POOL_NAME}:glow`
  {
    const size = instancedBufferAttribute<'float'>(aGlow, 'float')
    glowM.positionNode = origin
    const p11 = cameraProjectionMatrix.mul(vec4(0, 1, 0, 0)).y
    const pxToWorld = modelViewMatrix.mul(vec4(origin, 1)).z.negate().mul(2).div(p11.mul(1080))
    glowM.scaleNode = size.mul(max(float(1), pxToWorld.mul(PLUME.glowHaloPx * 2).div(max(size, 1e-4))))
    const axis = normalize(varying(modelWorldMatrix.mul(vec4(az, 0)).xyz, 'vPlumeGlowAxis')).negate()
    const toCam = normalize(cameraPosition.sub(varying(modelWorldMatrix.mul(vec4(origin, 1)).xyz, 'vPlumeGlowPos')))
    glowM.colorNode = glowColor(lib, drive, coreC, haloC, axis, toCam)
    glowM.opacityNode = float(1)
  }
  const glowSprite = new Sprite(glowM)
  glowSprite.name = `${PLUME_POOL_NAME}:glow`
  glowSprite.renderOrder = 12
  glowSprite.frustumCulled = false
  glowSprite.count = 0

  const object = new Group()
  object.name = PLUME_POOL_NAME
  object.add(haloMesh, coreMesh, glowSprite)
  object.visible = false

  function setCount(): void {
    haloMesh.count = n
    coreMesh.count = n
    glowSprite.count = n
    object.visible = n > 0
  }

  function markAll(): void {
    aOrigin.needsUpdate = true
    axisBuf.needsUpdate = true
    aDrive.needsUpdate = true
    colorBuf.needsUpdate = true
    aGlow.needsUpdate = true
  }

  function move(from: number, to: number): void {
    for (let k = 0; k < 3; k++) originArr[to * 3 + k] = originArr[from * 3 + k]
    for (let k = 0; k < 9; k++) axisArr[to * 9 + k] = axisArr[from * 9 + k]
    for (let k = 0; k < 4; k++) driveArr[to * 4 + k] = driveArr[from * 4 + k]
    for (let k = 0; k < 6; k++) colorArr[to * 6 + k] = colorArr[from * 6 + k]
    for (let k = 0; k < 12; k++) offset[to * 12 + k] = offset[from * 12 + k]
    glowArr[to] = glowArr[from]
    roots[to] = roots[from]
    plumes[to] = plumes[from]
    handleOf[to] = handleOf[from]
    indexOf[handleOf[to]] = to
  }

  function hide(i: number): void {
    for (let k = 0; k < 9; k++) axisArr[i * 9 + k] = 0
    glowArr[i] = 0
  }

  return {
    object,
    capacity: cap,
    origins: originArr,
    axes: axisArr,
    drives: driveArr,
    colors: colorArr,
    glowSizes: glowArr,
    get count(): number {
      return n
    },
    add(root: Object3D, plume: PooledPlume): number {
      if (freeTop === 0) {
        if (!warned) logger.warn('plume pool full; plume dropped', { capacity: cap })
        warned = true
        return -1
      }
      poolMatrix.identity()
      for (let o: Object3D | null = plume.group; o !== root; o = o.parent) {
        if (o === null) throw new Error(`plume ${plume.group.name} is not under root ${root.name}`)
        o.updateMatrix()
        poolMatrix.premultiply(o.matrix)
      }
      const e = poolMatrix.elements
      const slot = free[--freeTop]
      const i = n++
      handleOf[i] = slot
      indexOf[slot] = i
      roots[i] = root
      plumes[i] = plume
      for (let c = 0; c < 4; c++) for (let r = 0; r < 3; r++) offset[i * 12 + c * 3 + r] = e[c * 4 + r]
      // Linear working-space colours: what `uniform(new Color(hex))` carries on the per-ship path.
      poolColor.set(plume.coreHex)
      colorArr[i * 6] = poolColor.r
      colorArr[i * 6 + 1] = poolColor.g
      colorArr[i * 6 + 2] = poolColor.b
      poolColor.set(plume.haloHex)
      colorArr[i * 6 + 3] = poolColor.r
      colorArr[i * 6 + 4] = poolColor.g
      colorArr[i * 6 + 5] = poolColor.b
      for (let k = 0; k < 4; k++) driveArr[i * 4 + k] = 0
      hide(i)
      markAll()
      setCount()
      return slot
    },
    remove(slot: number): void {
      if (!Number.isInteger(slot) || slot < 0 || slot >= cap) return
      const i = indexOf[slot]
      if (i < 0) return
      const last = n - 1
      if (i !== last) move(last, i)
      roots[last] = null
      plumes[last] = null
      indexOf[slot] = -1
      free[freeTop++] = slot
      n = last
      markAll()
      setCount()
    },
    update(): void {
      const parent = object.parent
      for (let i = 0; i < n; i++) {
        const root = roots[i] as Object3D
        const plume = plumes[i] as PooledPlume
        const d = plume.drive
        driveArr[i * 4] = d.x
        driveArr[i * 4 + 1] = d.y
        driveArr[i * 4 + 2] = d.z
        driveArr[i * 4 + 3] = d.w
        if (root.parent !== parent || !shownUnder(plume.group, root)) {
          hide(i)
          continue
        }
        root.updateMatrix()
        const e = root.matrix.elements
        const o = i * 12
        // root × offset, column by column: the three axes (scaled r, r, len) and the origin.
        for (let c = 0; c < 4; c++) {
          const x = offset[o + c * 3]
          const y = offset[o + c * 3 + 1]
          const z = offset[o + c * 3 + 2]
          const rx = e[0] * x + e[4] * y + e[8] * z
          const ry = e[1] * x + e[5] * y + e[9] * z
          const rz = e[2] * x + e[6] * y + e[10] * z
          if (c === 3) {
            originArr[i * 3] = rx + e[12]
            originArr[i * 3 + 1] = ry + e[13]
            originArr[i * 3 + 2] = rz + e[14]
            continue
          }
          const s = c === 2 ? plume.len : plume.radius
          axisArr[i * 9 + c * 3] = rx * s
          axisArr[i * 9 + c * 3 + 1] = ry * s
          axisArr[i * 9 + c * 3 + 2] = rz * s
          if (c === 0) glowArr[i] = plume.radius * PLUME.glowSize * Math.sqrt(rx * rx + ry * ry + rz * rz)
        }
      }
      if (n > 0) markAll()
    },
    dispose(): void {
      object.removeFromParent()
      ;(haloMesh.material as Material).dispose()
      ;(coreMesh.material as Material).dispose()
      glowM.dispose()
      for (let i = 0; i < n; i++) {
        roots[i] = null
        plumes[i] = null
      }
      n = 0
      setCount()
    },
  }
}

/**
 * A one-plume pool at full drive for the pipeline warm-up (`buildPlumeWarmHolder`): the pool's code does not depend on
 * its capacity or count (per-instance vertex attributes), so it builds the live pool's pipelines. Rare: allocates.
 */
function plumePoolWarmGroup(lib: MaterialLibrary, faction: FactionId): Group {
  const holder = new Group()
  holder.name = 'plume:warm:pool'
  const pool = createPlumePool(lib, 1)
  const root = new Group()
  const group = new Group()
  root.add(group)
  holder.add(pool.object, root)
  const colors = driveColors(lib, faction)
  pool.add(root, { group, radius: 1, retro: false, drive: new Vector4(1, 1, 0, 0), len: plumeLength(1, 1, true, false), coreHex: colors.core, haloHex: colors.halo })
  pool.update()
  return holder
}

/**
 * `pooled` (PERF L-M4 part 3, ship views with `materials.json plumes.pooled`): build only the per-plume heat shimmer;
 * the caller registers `plumes` with a plume pool, which draws the halo and core shells and the glow.
 */
export function attachEngineFx(lib: MaterialLibrary, instance: ModelInstance, faction: FactionId, pooled = false): EngineFx {
  const main: Plume[] = []
  const retro: Plume[] = []
  addPlumes(lib, faction, instance.sockets, 'ENG_MAIN_', false, pooled, main)
  addPlumes(lib, faction, instance.sockets, 'ENG_RETRO_', true, pooled, retro)
  const li = internals(lib)
  const plumes: readonly PooledPlume[] = pooled ? [...main, ...retro] : []
  return {
    plumes,
    setThrust(mainAmount: number, retroAmount: number, boost: boolean): void {
      // Heat shimmer costs a framebuffer copy: High only.
      const shimmerOn = li.preset === 'high'
      driveAmount[0] = mainAmount
      drivePlumes(main, boost, shimmerOn)
      driveAmount[0] = retroAmount
      drivePlumes(retro, false, shimmerOn)
    },
    setControl(): void {},
    dispose(): void {
      for (const p of main) p.group.removeFromParent()
      for (const p of retro) p.group.removeFromParent()
    },
  }
}
